import React from "react";
import styled from "styled-components";
import Tile from "../Tile";
import { PILE, spriteKey } from "../../engine/solitaire";

// The shared drag-PRESENTATION core: everything both drag experiences
// need — the player's own pointer-driven drag and the spectated
// wire-driven one — lives here exactly once. Callers own the input
// adapter: PlayableSolitaire wrangles real DOM pointer events (capture,
// click suppression, drop resolution); SpectatedSolitaire replays
// remote cursor frames. One core, two thin adapters — NOT one machine
// with isRemote flags.

// Cascade spacing for the ghost — matches TableauStack's face-up fan.
export const GHOST_FAN_OFFSET = 16;
// Movement (px) that turns a press into a drag rather than a click.
export const DRAG_THRESHOLD_PX = 5;

// data-card-source tags: "w" (waste top), "f:i" (foundation top),
// "t:col:depth" (tableau card, depth 0 = the fan's top/last card).
export function parseSourceTag(tag) {
  if (tag === "w") return { pile: PILE.WASTE };
  const [kind, a, b] = tag.split(":");
  if (kind === "f") return { pile: PILE.FOUNDATION, index: Number(a) };
  if (kind === "t")
    return { pile: PILE.TABLEAU, index: Number(a), depth: Number(b) };
  return null;
}

export function sameSource(a, b) {
  if (!a || !b || a.pile !== b.pile) return false;
  if (a.pile === PILE.WASTE) return true;
  if (a.pile === PILE.FOUNDATION) return a.index === b.index;
  if (a.pile === PILE.TABLEAU)
    return a.index === b.index && a.depth === b.depth;
  return false;
}

// Sprite names for the card(s) a grab at src picks up — a tableau grab
// takes the card and everything fanned below it (partial-run rule).
export function cardsForSource(state, src) {
  if (src.pile === PILE.WASTE) {
    return [spriteKey(state.waste[state.waste.length - 1])];
  }
  if (src.pile === PILE.FOUNDATION) {
    const f = state.foundations[src.index];
    return [spriteKey(f[f.length - 1])];
  }
  const faceUp = state.tableau[src.index].faceUp;
  return faceUp.slice(faceUp.length - 1 - src.depth).map(spriteKey);
}

// The DOM elements a grab at src lifts off the board (to hide while the
// ghost is in flight). grabEl seeds the non-tableau cases.
export function sourceElements(root, src, grabEl) {
  if (!root) return grabEl ? [grabEl] : [];
  if (src.pile !== PILE.TABLEAU) {
    if (grabEl) return [grabEl];
    const el = root.querySelector(
      `[data-card-source="${src.pile === PILE.WASTE ? "w" : `f:${src.index}`}"]`,
    );
    return el ? [el] : [];
  }
  const els = [];
  for (let depth = 0; depth <= src.depth; depth++) {
    const el = root.querySelector(
      `[data-card-source="t:${src.index}:${depth}"]`,
    );
    if (el) els.push(el);
  }
  return els;
}

export function hideElements(els) {
  for (const el of els) el.style.visibility = "hidden";
}

export function restoreElements(els) {
  for (const el of els) el.style.visibility = "";
}

// Hit-test a point (client px) against the tagged cards under root.
// Containment first — the LAST match wins, because document order is
// paint order and the top card of a fan is last. Falls back to the
// nearest card center within radiusPx (client px) — remote presses can
// land a hair off the card that was really grabbed (bot jitter, tab
// scaling), and a silent miss reads worse than a near-miss snap.
export function hitTestCard(root, clientX, clientY, radiusPx = 0) {
  if (!root) return null;
  let contained = null;
  let nearest = null;
  let nearestDist = Infinity;
  for (const el of root.querySelectorAll("[data-card-source]")) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue;
    if (
      clientX >= r.left &&
      clientX <= r.right &&
      clientY >= r.top &&
      clientY <= r.bottom
    ) {
      contained = el;
      continue;
    }
    if (radiusPx > 0) {
      const dx = clientX - (r.left + r.width / 2);
      const dy = clientY - (r.top + r.height / 2);
      const d = Math.hypot(dx, dy);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = el;
      }
    }
  }
  const el = contained ?? (nearestDist <= radiusPx ? nearest : null);
  if (!el) return null;
  const src = parseSourceTag(el.getAttribute("data-card-source"));
  return src ? { src, el } : null;
}

// The cards in flight — just the fanned stack; the caller owns the
// positioned wrapper (own game: fixed-position portal riding the real
// pointer; spectated: absolute inside the window, riding the remote
// cursor's interpolation).
export function GhostCards({ cards }) {
  return cards.map((name, i) => (
    <GhostSlot key={i} style={{ top: i * GHOST_FAN_OFFSET + "px" }}>
      <Tile name={name} />
    </GhostSlot>
  ));
}

const GhostSlot = styled.div`
  position: absolute;
  left: 0;
`;
